import { AlexiaPage } from './pages/AlexiaPage';
import { ShareTargetPage } from './pages/ShareTargetPage';
import { WhatsAppDemoPage } from './pages/WhatsAppDemoPage';
import { DraftsPage, DraftReviewPage } from './pages/DraftsPage';
import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useMe } from './api/hooks';
import type { PermissionModule } from '@alexiarest/shared';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { portadaDe, veSeccion } from './auth/roles';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { LoadingState } from './components/EmptyState';
import { ToastProvider } from './components/Toast';
import { PeriodProvider } from './context/PeriodContext';
import { LocationProvider } from './context/LocationContext';
import { ThemeProvider } from './context/ThemeContext';
import { LoginPage } from './pages/LoginPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { setupPending } from './pages/setupShell';

/**
 * El resto de pantallas se cargan al navegar a ellas. Sin esto, abrir el
 * panel desde un movil descargaba las 14 pantallas y Recharts entero
 * (~900 KB) antes de pintar un solo numero.
 */
/**
 * `lazy` con red de seguridad para deploys: los chunks llevan hash y un
 * deploy los renombra. Si un navegador con la app abierta de antes pide un
 * chunk que ya no existe, en vez de una pagina en blanco se recarga UNA vez
 * (flag en sessionStorage para no entrar en bucle) y coge el index nuevo.
 */
function lazyPage<T extends object>(
  load: () => Promise<T>,
  name: keyof T & string,
): LazyExoticComponent<ComponentType> {
  /*
   * Un chunk que no se puede cargar es casi siempre un index.html de antes
   * de un despliegue. Se recarga la pagina para coger el index nuevo, con
   * una marca de tiempo para no entrar en bucle: si la recarga cae dentro
   * de la ventana del propio despliegue (invalidacion de CloudFront en
   * curso), a los 30 s se permite otra. Antes la marca no caducaba y el
   * segundo fallo ensenyaba el error tal cual (Alex, 6 sep 2026).
   */
  const CLAVE = 'alexia:chunk-reload';
  return lazy(() =>
    load()
      .then((m) => {
        sessionStorage.removeItem(CLAVE);
        return { default: m[name] as unknown as ComponentType };
      })
      .catch((err: unknown) => {
        const ultima = Number(sessionStorage.getItem(CLAVE) ?? 0);
        if (Date.now() - ultima > 30_000) {
          sessionStorage.setItem(CLAVE, String(Date.now()));
          window.setTimeout(() => window.location.reload(), 1500);
          // Mientras tanto no se ensenya el error: la recarga viene en camino.
          return new Promise<{ default: ComponentType }>(() => undefined);
        }
        throw err;
      }),
  );
}

const AdminPage = lazyPage(() => import('./pages/AdminPage'), 'AdminPage');
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'), 'DashboardPage');
const AnalysisPage = lazyPage(() => import('./pages/AnalysisPage'), 'AnalysisPage');
const ExpensesPage = lazyPage(() => import('./pages/ExpensesPage'), 'ExpensesPage');
const IngredientsPage = lazyPage(() => import('./pages/IngredientsPage'), 'IngredientsPage');
const InventoryPage = lazyPage(() => import('./pages/InventoryPage'), 'InventoryPage');
const InvestmentsPage = lazyPage(() => import('./pages/InvestmentsPage'), 'InvestmentsPage');
const PricingPage = lazyPage(() => import('./pages/PricingPage'), 'PricingPage');
const RecipeDetailPage = lazyPage(() => import('./pages/RecipeDetailPage'), 'RecipeDetailPage');
const RecipesPage = lazyPage(() => import('./pages/RecipesPage'), 'RecipesPage');
const ResultsPage = lazyPage(() => import('./pages/ResultsPage'), 'ResultsPage');
const SalesPage = lazyPage(() => import('./pages/SalesPage'), 'SalesPage');
const SettingsPage = lazyPage(() => import('./pages/SettingsPage'), 'SettingsPage');
const PosCallbackPage = lazyPage(() => import('./pages/PosCallbackPage'), 'PosCallbackPage');
const SuppliersPage = lazyPage(() => import('./pages/SuppliersPage'), 'SuppliersPage');
const FounderPage = lazyPage(() => import('./pages/FounderPage'), 'FounderPage');
const SetupWizardPage = lazyPage(() => import('./pages/SetupWizardPage'), 'SetupWizardPage');
const TargetsPage = lazyPage(() => import('./pages/TargetsPage'), 'TargetsPage');
const TaxesPage = lazyPage(() => import('./pages/TaxesPage'), 'TaxesPage');
const TreasuryPage = lazyPage(() => import('./pages/TreasuryPage'), 'TreasuryPage');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Guarda de la zona de administracion. La entrada del menu ya se oculta a
 * quien no es admin, pero la URL se puede teclear: sin esto, un cliente
 * curioso (o la demo publica) veria el armazon del panel antes del 403.
 */
function SoloAdmin(): JSX.Element {
  const me = useMe();
  if (me.isPending) return <LoadingState label="Comprobando permisos…" />;
  if (!me.data?.user?.platformAdmin) return <Navigate to="/" replace />;
  return <AdminPage />;
}

/**
 * Pantallas cerradas a un rol: la API ya le responde 403, y esta guarda
 * evita ensenyarle el armazon si teclea la URL. Se le lleva a su portada.
 *
 * Una sola guarda para todas las pantallas (mejora 70): antes habia dos
 * —«sin P&L» y «sin financiero»— con la lista de roles escrita dentro. Ahora
 * cada ruta dice a que SECCION pertenece y `veSeccion` decide, que es lo
 * unico que sabe responder igual para un rol fijo y para uno personalizado.
 */
function Seccion({
  modulo,
  children,
}: {
  modulo: PermissionModule;
  children: JSX.Element;
}): JSX.Element {
  const me = useMe();
  if (me.isPending) return <LoadingState label="Comprobando permisos…" />;
  const acceso = { role: me.data?.user?.role, permissions: me.data?.user?.permissions };
  if (!veSeccion(acceso, modulo)) return <Navigate to={portadaDe(acceso)} replace />;
  return children;
}

/**
 * Alexia es UNA conversación (14 sep 2026): la abre quien puede contarle
 * recetas o gastos y también quien solo puede preguntarle (la gestoría).
 */
function SeccionAlexia({ children }: { children: JSX.Element }): JSX.Element {
  const me = useMe();
  if (me.isPending) return <LoadingState label="Comprobando permisos…" />;
  const acceso = { role: me.data?.user?.role, permissions: me.data?.user?.permissions };
  const puede = veSeccion(acceso, 'asistente') || veSeccion(acceso, 'recetas') || veSeccion(acceso, 'gastos');
  if (!puede) return <Navigate to={portadaDe(acceso)} replace />;
  return children;
}

/** Redirección que conserva la consulta (`?q=` con una pregunta ya escrita). */
function RedirigirConsulta({ a }: { a: string }): JSX.Element {
  const location = useLocation();
  return <Navigate to={`${a}${location.search}`} replace />;
}

/**
 * Cerrojo Founder: quien se da de alta con invitación no tiene prueba
 * gratuita. Hasta que Stripe confirma el pago solo existe la página de pago —
 * sin menú, sin rutas, sin escapatoria—. El flag `founderPending` lo pone el
 * onboarding y lo quita el webhook al activarse la suscripción.
 */
function CerrojoFounder({ children }: { children: JSX.Element }): JSX.Element {
  const me = useMe();
  const { logout } = useAuth();
  if (me.isPending) return <LoadingState label="Cargando tu cuenta…" />;
  if (!me.data?.tenant?.founderPending) return children;
  return (
    <div className="founder-lock">
      <div className="founder-lock__bar">
        <span>Tu plaza Founder está reservada. Completa el pago para entrar en Alexia.</span>
        <button type="button" className="btn btn--ghost" onClick={logout}>
          Cerrar sesión
        </button>
      </div>
      <ErrorBoundary>
        <Suspense fallback={<LoadingState label="Cargando…" />}>
          <FounderPage />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

/**
 * Cuestionario de configuracion inicial (Alex, 5 sep 2026): despues del
 * alta y del pago Founder, y antes de la app, el propietario responde los
 * pasos que faltan. Se guarda en el tenant, asi que sobrevive a cerrar el
 * navegador. La vuelta del OAuth del TPV (paso 4) tiene que pasar: esa ruta
 * se deja entrar aunque el cuestionario siga abierto.
 */
function CuestionarioInicial({ children }: { children: JSX.Element }): JSX.Element {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <LoadingState label="Cargando tu cuenta…" />;
  const enCallbackTpv = location.pathname.startsWith('/integraciones/');
  if (!setupPending(me.data?.tenant, me.data?.user?.role) || enCallbackTpv) return children;
  // Con su propio boundary: aqui no hay menu al que escapar, y un fallo de
  // render sin red dejaba la pagina en blanco (7 sep 2026).
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingState label="Cargando…" />}>
        <SetupWizardPage />
      </Suspense>
    </ErrorBoundary>
  );
}

/** La portada lleva la P&L dentro: al encargado se le lleva a Ingresos y a cocina a Recetas. */
function PortadaPorRol(): JSX.Element {
  const me = useMe();
  if (me.isPending) return <LoadingState label="Cargando…" />;
  const acceso = { role: me.data?.user?.role, permissions: me.data?.user?.permissions };
  if (!veSeccion(acceso, 'panel')) return <Navigate to={portadaDe(acceso)} replace />;
  return <DashboardPage />;
}

function AppRoutes(): JSX.Element {
  const { loading, isAuthenticated, needsOnboarding } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingState label="Comprobando tu sesión…" />;
  if (!isAuthenticated) return <LoginPage />;
  // Sesion valida pero sin restaurante: primero el alta, luego la app.
  if (needsOnboarding) return <OnboardingPage />;

  return (
    // El boundary va DENTRO del router (necesita la ruta para resetearse) y
    // por fuera de las pantallas: un fallo de una no tumba la aplicacion.
    //
    // LocationProvider envuelve TAMBIEN el cerrojo Founder y el cuestionario:
    // los hooks de datos (useSaveTarget y compania) leen el local activo, y
    // el paso 5 del cuestionario los usa. Con el provider por dentro, el
    // primer cliente que llego a ese paso tras el multi-local (Mulita, 7 sep
    // 2026) se quedo con la pantalla en blanco.
    <LocationProvider>
    <CerrojoFounder>
    <CuestionarioInicial>
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingState label="Cargando…" />}>
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<PortadaPorRol />} />
        <Route path="alexia" element={<SeccionAlexia><AlexiaPage /></SeccionAlexia>} />
        {/* Las dos Alexias de antes (14 sep 2026): los enlaces guardados siguen llegando. */}
        <Route path="asistente" element={<RedirigirConsulta a="/alexia" />} />
        <Route path="chat" element={<RedirigirConsulta a="/alexia" />} />
        <Route path="ingredientes" element={<Seccion modulo="ingredientes"><IngredientsPage /></Seccion>} />
        <Route path="inventario" element={<Seccion modulo="inventario"><InventoryPage /></Seccion>} />
        <Route path="proveedores" element={<Seccion modulo="proveedores"><SuppliersPage /></Seccion>} />
        <Route path="recetas" element={<Seccion modulo="recetas"><RecipesPage /></Seccion>} />
        <Route path="compartir" element={<ShareTargetPage />} />
        <Route path="demo-whatsapp" element={<WhatsAppDemoPage />} />
        <Route path="borradores" element={<DraftsPage />} />
        <Route path="borradores/:id" element={<DraftReviewPage />} />
        <Route path="recetas/:id" element={<Seccion modulo="recetas"><RecipeDetailPage /></Seccion>} />
        <Route path="precios" element={<Seccion modulo="precios"><PricingPage /></Seccion>} />
        <Route path="ingresos" element={<Seccion modulo="ingresos"><SalesPage /></Seccion>} />
        <Route path="gastos" element={<Seccion modulo="gastos"><ExpensesPage /></Seccion>} />
        <Route path="analisis" element={<Seccion modulo="analisis"><AnalysisPage /></Seccion>} />
        <Route path="resultados" element={<Seccion modulo="resultados"><ResultsPage /></Seccion>} />
        <Route path="tesoreria" element={<Seccion modulo="tesoreria"><TreasuryPage /></Seccion>} />
        <Route path="inversiones" element={<Seccion modulo="inversiones"><InvestmentsPage /></Seccion>} />
        <Route path="objetivos" element={<Seccion modulo="objetivos"><TargetsPage /></Seccion>} />
        <Route path="impuestos" element={<Seccion modulo="impuestos"><TaxesPage /></Seccion>} />
        <Route path="ajustes" element={<SettingsPage />} />
        {/* Vuelta del OAuth de un TPV (Square, Loyverse). Ver PosCallbackPage. */}
        <Route path="integraciones/:provider/callback" element={<PosCallbackPage />} />
        {/* Oferta Founder: fuera del menú, se llega por enlace directo. */}
        <Route path="founder" element={<FounderPage />} />
        <Route path="admin" element={<SoloAdmin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
      </Suspense>
    </ErrorBoundary>
    </CuestionarioInicial>
    </CerrojoFounder>
    </LocationProvider>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
        <AuthProvider>
          <PeriodProvider>
            <BrowserRouter>
              {import.meta.env.VITE_APP_ENV === 'dev' && (
                <div className="dev-environment-bar" role="status">
                  <strong>DEV</strong>
                  <span>Entorno privado de Alex y Pol</span>
                  <span aria-hidden="true">·</span>
                  <span>Datos de prueba</span>
                  <span aria-hidden="true">·</span>
                  <span>IA real</span>
                </div>
              )}
              <AppRoutes />
            </BrowserRouter>
          </PeriodProvider>
        </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
